// Static state-level metadata powering /first-time-buyer/:state programmatic SEO pages.
// Bet 5 of the organic-growth roadmap. Reuses STATE_GRANTS for base HFA info and
// overlays a flagship-program name + program-shape summary per state.
//
// Real dollar caps and program terms are intentionally NOT quoted — they change
// every fiscal year. Named flagship programs are stable public info.

import { STATE_GRANTS, type StateGrantInfo } from "./statesGrants";

export interface StateFirstTimeBuyerInfo extends StateGrantInfo {
  /** Flagship state FTHB program name (headline product from the HFA). */
  flagshipProgram: string;
  /** Short benefit summary — 1 sentence, no dollar amounts. */
  benefitSummary: string;
  /** Common minimum credit score requirement, expressed as text. */
  creditFloor: string;
  /** Federal loan types the state program typically pairs with. */
  combinesWith: string;
}

// Named flagship programs per state HFA. Where no override exists, we fall back
// to `${hfaShort} First-Time Homebuyer Program` — still unique-per-page because
// it composes with the HFA short name.
const FLAGSHIP_OVERRIDES: Record<string, string> = {
  "alabama": "AHFA Step Up",
  "alaska": "AHFC First-Time Homebuyer Program",
  "arizona": "Home Plus (Arizona IDA)",
  "arkansas": "ADFA Move-Up Choice",
  "california": "CalHFA MyHome Assistance",
  "colorado": "CHFA FirstStep",
  "connecticut": "CHFA Time to Own",
  "delaware": "DSHA Welcome Home",
  "district-of-columbia": "DC Open Doors",
  "florida": "Florida Housing Homebuyer Program",
  "georgia": "Georgia Dream Homeownership",
  "hawaii": "HHFDC Mortgage Credit Certificate",
  "idaho": "Idaho Housing First Loan",
  "illinois": "IHDA Access Forgivable",
  "indiana": "IHCDA First Place",
  "iowa": "IFA FirstHome Plus",
  "kansas": "First Time Homebuyer Program (KHRC)",
  "kentucky": "KHC Regular Down Payment Assistance",
  "louisiana": "LHC Market Rate GNMA",
  "maine": "MaineHousing First Home Loan",
  "maryland": "Maryland Mortgage Program",
  "massachusetts": "MassHousing ONE Mortgage",
  "michigan": "MSHDA MI Home Loan",
  "minnesota": "Minnesota Housing Start Up",
  "mississippi": "MHC Smart6",
  "missouri": "MHDC First Place Loan",
  "montana": "Montana Housing Regular Bond Program",
  "nebraska": "NIFA First Home Program",
  "nevada": "Home Is Possible",
  "new-hampshire": "New Hampshire Housing Home Flex Plus",
  "new-jersey": "NJHMFA First-Time Homebuyer Mortgage",
  "new-mexico": "MFA FIRSTHome",
  "new-york": "SONYMA Achieving the Dream",
  "north-carolina": "NC Home Advantage Mortgage",
  "north-dakota": "NDHFA FirstHome",
  "ohio": "OHFA Your Choice",
  "oklahoma": "OHFA Homebuyer Down Payment Assistance",
  "oregon": "OHCS Oregon Bond Residential Loan",
  "pennsylvania": "PHFA Keystone Home Loan",
  "rhode-island": "RIHousing FirstHomes",
  "south-carolina": "SC Housing Homebuyer Program",
  "south-dakota": "SDHDA First-Time Homebuyer Program",
  "tennessee": "THDA Great Choice Home Loan",
  "texas": "My First Texas Home (TDHCA)",
  "utah": "Utah Housing FirstHome Loan",
  "vermont": "VHFA MOVE Mortgage",
  "virginia": "Virginia Housing Down Payment Assistance Grant",
  "washington": "WSHFC Home Advantage",
  "west-virginia": "WVHDF Homeownership Program",
  "wisconsin": "WHEDA Advantage Conventional",
  "wyoming": "WCDA Standard First-Time Homebuyer",
};

function flagshipFor(s: StateGrantInfo): string {
  return FLAGSHIP_OVERRIDES[s.slug] ?? `${s.hfaShort} First-Time Homebuyer Program`;
}

function summaryFor(s: StateGrantInfo): string {
  return `Below-market first mortgage from ${s.hfaShort} paired with down payment and closing-cost assistance, plus an optional MCC tax credit. Available to qualified ${s.name} first-time buyers meeting income and purchase-price caps.`;
}

export const STATE_FIRST_TIME_BUYER: StateFirstTimeBuyerInfo[] = STATE_GRANTS.map((s) => ({
  ...s,
  flagshipProgram: flagshipFor(s),
  benefitSummary: summaryFor(s),
  creditFloor: "typically 620–640 for conventional, 580 for FHA",
  combinesWith: "FHA, VA, USDA, and conventional first mortgages",
}));

export function getStateFthbBySlug(slug: string | undefined): StateFirstTimeBuyerInfo | undefined {
  if (!slug) return undefined;
  const norm = slug.toLowerCase();
  return STATE_FIRST_TIME_BUYER.find((s) => s.slug === norm);
}

export function getSiblingFthbStates(slug: string, count = 3): StateFirstTimeBuyerInfo[] {
  const i = STATE_FIRST_TIME_BUYER.findIndex((s) => s.slug === slug);
  if (i === -1) return [];
  const out: StateFirstTimeBuyerInfo[] = [];
  for (let k = 1; k <= count; k++) {
    out.push(STATE_FIRST_TIME_BUYER[(i + k) % STATE_FIRST_TIME_BUYER.length]);
  }
  return out;
}